/**
 * ImageUploader - Reusable component for selecting/cropping/uploading images.
 *
 * Two usage modes:
 *
 * 1. Managed (direct upload): pass an `uploadEndpoint` and the component uploads
 *    automatically after cropping.
 *
 * 2. Unmanaged (deferred upload): pass an `onImageSelected` callback.  The caller
 *    gets the cropped Blob and decides when/where to upload it.
 *
 * Two display modes, toggled internally:
 *
 * - Preview mode (default, when a `currentImageUrl` is given): shows the
 *   image with a ✎ edit button (switches to edit mode) and a round ×
 *   delete button (two-click arm/confirm, same pattern as elsewhere).
 * - Edit mode (default when there's no current image, or after clicking
 *   ✎): shows the upload/URL tabs. When reached from preview mode, a small
 *   × (not the round delete button — canceling is harmless, no confirm
 *   needed) discards the in-progress edit and returns to preview.
 */
import { ImageCropper } from './ImageCropper.js';
import { authService } from '../../auth/AuthService.js';

const VALID_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export interface ImageUploaderOptions {
  /** Container element to render into */
  container: HTMLElement;

  /** Optional id prefix for nested elements (to avoid collisions if multiple uploaders exist). */
  idPrefix?: string;

  /** Current image URL to show as preview (existing image) */
  currentImageUrl?: string;

  /** Called when a cropped image is ready.
   *  In managed mode the `response` from the server is also provided
   *  so the caller can update local data. */
  onImageSelected?: (croppedBlob: Blob, response?: { imageUrl: string }) => void;

  /** Called when the user removes the current image */
  onImageRemoved?: () => void;

  /** Managed mode: upload endpoint (e.g. '/api/ingredient/abc123/image').
   *  When provided, the component uploads directly and calls onImageSelected
   *  with the uploaded blob after success. */
  uploadEndpoint?: string;

  /** Whether to hide the "Add image" / "Change image" label. */
  hideLabel?: boolean;
}

export class ImageUploader {
  private container: HTMLElement;
  private options: ImageUploaderOptions;
  private errorEl: HTMLElement | null = null;
  private idPrefix: string;
  private removeBtn: HTMLButtonElement | null = null;
  private removeBtnArmed: boolean = false;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  /** Whether the edit (upload/URL tabs) view is showing instead of the preview. */
  private editingImage: boolean = false;

  private static readonly REMOVE_BTN_NORMAL_CLASSES = ['bg-transparent', 'border-[var(--accent-alpha-40)]', 'text-[var(--accent)]'];
  private static readonly REMOVE_BTN_ARMED_CLASSES = ['bg-[var(--accent)]', 'border-[var(--accent)]', 'text-[#0d1117]'];

  constructor(options: ImageUploaderOptions) {
    this.container = options.container;
    this.options = options;
    this.idPrefix = options.idPrefix || 'uploader';
  }

  /**
   * Build the UI inside the container. Safe to call more than once — it
   * clears and rebuilds (used internally to toggle preview/edit mode).
   */
  render(): void {
    this.container.innerHTML = '';
    this.removeBtn = null;
    this.errorEl = null;

    if (this.options.currentImageUrl && !this.editingImage) {
      this.renderPreviewMode();
    } else {
      this.renderEditMode();
    }
  }

  /**
   * Preview mode: the current image with ✎ (edit) and × (delete) buttons.
   */
  private renderPreviewMode(): void {
    const { currentImageUrl } = this.options;

    const previewDiv = document.createElement('div');
    previewDiv.className = 'relative w-48 h-48 mx-auto mb-4';

    const img = document.createElement('img');
    img.src = currentImageUrl!;
    img.alt = 'Current image';
    img.className = 'w-full h-full object-cover rounded-full border-2 border-gray-200 block';
    img.onerror = () => { img.style.display = 'none'; };
    previewDiv.appendChild(img);

    const btnGroup = document.createElement('div');
    btnGroup.className = 'absolute top-0 flex gap-1';
    btnGroup.style.right = '-12px';

    // Edit button — pencil to the left of the delete button, consistent
    // with the edit/delete pairing used elsewhere (e.g. List View).
    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.title = 'Canvia la imatge';
    editBtn.className =
      `border text-sm w-7 h-7 flex items-center justify-center rounded-full ` +
      `focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-colors ${ImageUploader.REMOVE_BTN_NORMAL_CLASSES.join(' ')}`;
    const editGlyph = document.createElement('span');
    editGlyph.textContent = '✎';
    editGlyph.style.cssText = 'display: inline-block;';
    editBtn.appendChild(editGlyph);
    editBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.editingImage = true;
      this.render();
    });
    btnGroup.appendChild(editBtn);

    // Delete button — two-click arm/confirm, same pattern and accent
    // color as the round delete buttons elsewhere (Graph View, List View).
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className =
      `border text-sm w-7 h-7 flex items-center justify-center rounded-full ` +
      `focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-colors ${ImageUploader.REMOVE_BTN_NORMAL_CLASSES.join(' ')}`;
    removeBtn.title = 'Esborra la imatge';
    const removeBtnGlyph = document.createElement('span');
    removeBtnGlyph.textContent = '×';
    removeBtnGlyph.style.cssText = 'display: inline-block; transform: translateY(1px);';
    removeBtn.appendChild(removeBtnGlyph);
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.removeBtnArmed) {
        this.disarmRemoveBtn();
        this.handleRemove();
      } else {
        this.armRemoveBtn();
      }
    });
    this.removeBtn = removeBtn;
    btnGroup.appendChild(removeBtn);

    previewDiv.appendChild(btnGroup);
    this.container.appendChild(previewDiv);
  }

  /**
   * Edit mode: upload/URL tabs. Reached either as the default (no current
   * image) or via the ✎ button (with a current image to cancel back to).
   */
  private renderEditMode(): void {
    const { currentImageUrl } = this.options;

    // Cancel — only shown when there's a current image to revert to.
    // A small inline × (not the round delete button): canceling an
    // in-progress edit is harmless and reversible, so no confirm needed.
    if (currentImageUrl) {
      const cancelRow = document.createElement('div');
      cancelRow.className = 'flex justify-end';
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'text-[var(--accent)] hover:text-red-400 font-bold leading-none text-lg';
      cancelBtn.textContent = '×';
      cancelBtn.title = 'Cancel·la i mantén la imatge actual';
      cancelBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.editingImage = false;
        this.render();
      });
      cancelRow.appendChild(cancelBtn);
      this.container.appendChild(cancelRow);
    }

    // Label (optional)
    if (!this.options.hideLabel) {
      const label = document.createElement('label');
      label.className = 'block text-sm font-medium text-gray-700 mb-1';
      label.textContent = currentImageUrl ? 'Canvia la imatge' : 'Afegeix una imatge';
      this.container.appendChild(label);
    }

    // Tab buttons
    const tabsDiv = document.createElement('div');
    tabsDiv.className = 'flex justify-center border-b border-gray-200 mb-3';

    const uploadTab = document.createElement('button');
    uploadTab.type = 'button';
    uploadTab.className = 'px-4 py-2 text-sm font-medium border-b-2 border-blue-600 text-blue-600';
    uploadTab.textContent = 'Puja un fitxer';
    uploadTab.dataset.tab = 'upload';
    tabsDiv.appendChild(uploadTab);

    const urlTab = document.createElement('button');
    urlTab.type = 'button';
    urlTab.className = 'px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700';
    urlTab.textContent = 'URL externa';
    urlTab.dataset.tab = 'url';
    tabsDiv.appendChild(urlTab);

    this.container.appendChild(tabsDiv);

    // Upload panel
    const uploadPanel = document.createElement('div');
    uploadPanel.id = `${this.idPrefix}-upload-panel`;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/webp,image/gif';
    fileInput.className =
      'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
    fileInput.addEventListener('change', () => this.handleFileSelect(fileInput));
    uploadPanel.appendChild(fileInput);

    const help = document.createElement('p');
    help.className = 'text-xs text-gray-500 mt-1';
    help.textContent = 'Màx. 10 MB. Formats: JPG, PNG, WebP, GIF';
    uploadPanel.appendChild(help);

    this.container.appendChild(uploadPanel);

    // URL panel (hidden by default)
    const urlPanel = document.createElement('div');
    urlPanel.id = `${this.idPrefix}-url-panel`;
    urlPanel.className = 'hidden';

    const urlRow = document.createElement('div');
    urlRow.className = 'flex gap-2';

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.className =
      'flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
    urlInput.placeholder = 'https://example.com/image.jpg';
    urlRow.appendChild(urlInput);

    const okBtn = document.createElement('button');
    okBtn.type = 'button';
    okBtn.className =
      'px-4 py-2 bg-gray-700 text-white text-sm rounded-md hover:bg-gray-600 whitespace-nowrap';
    okBtn.textContent = 'D\'acord';
    okBtn.addEventListener('click', () => this.handleFetchAndCrop(urlInput, okBtn));
    urlRow.appendChild(okBtn);

    urlPanel.appendChild(urlRow);

    const urlHelp = document.createElement('p');
    urlHelp.className = 'text-xs text-gray-500 mt-1';
    urlHelp.textContent = 'La imatge es descarregarà, retallarà i pujarà.';
    urlPanel.appendChild(urlHelp);

    this.container.appendChild(urlPanel);

    // Error container
    this.errorEl = document.createElement('div');
    this.errorEl.className = 'hidden text-red-600 text-xs bg-red-50 border border-red-200 rounded-md p-2 mt-2';
    this.container.appendChild(this.errorEl);

    // Tab switching
    uploadTab.addEventListener('click', () => this.switchTab('upload'));
    urlTab.addEventListener('click', () => this.switchTab('url'));
  }

  // ─── Handlers ───────────────────────────────────────────────

  private async handleFileSelect(fileInput: HTMLInputElement): Promise<void> {
    const file = fileInput.files?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      this.showError('El fitxer és massa gran. La mida màxima és de 10 MB');
      fileInput.value = '';
      return;
    }

    if (!VALID_TYPES.includes(file.type)) {
      this.showError('Tipus de fitxer no vàlid. Utilitza JPG, PNG, WebP o GIF');
      fileInput.value = '';
      return;
    }

    this.clearError();

    const cropper = new ImageCropper();
    const croppedBlob = await cropper.open(file);

    if (!croppedBlob) {
      fileInput.value = ''; // user cancelled
      return;
    }

    await this.processCroppedImage(croppedBlob);
    fileInput.value = ''; // reset so the same file can be re-selected
  }

  private async handleFetchAndCrop(
    urlInput: HTMLInputElement,
    fetchBtn: HTMLButtonElement
  ): Promise<void> {
    const url = urlInput.value?.trim();
    if (!url) {
      this.showError('Introdueix una URL d\'imatge');
      return;
    }

    try {
      new URL(url);
      if (!url.startsWith('https://')) {
        this.showError('La URL de la imatge ha d\'utilitzar HTTPS');
        return;
      }
    } catch {
      this.showError('Format de URL no vàlid');
      return;
    }

    this.clearError();
    fetchBtn.disabled = true;
    fetchBtn.textContent = 'Descarregant...';

    try {
      const response = await fetch('/api/images/fetch-from-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders(),
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Fetch failed' }));
        throw new Error(errorData.detail || 'Failed to fetch image');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'fetched.jpg';
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

      const cropper = new ImageCropper();
      const croppedBlob = await cropper.open(file);

      if (!croppedBlob) return; // user cancelled

      fetchBtn.textContent = 'Pujant...';
      await this.processCroppedImage(croppedBlob);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'No s\'ha pogut descarregar la imatge');
    } finally {
      fetchBtn.disabled = false;
      fetchBtn.textContent = 'D\'acord';
    }
  }

  /**
   * Process a cropped image: upload if managed, or pass to callback.
   */
  private async processCroppedImage(croppedBlob: Blob): Promise<void> {
    if (this.options.uploadEndpoint) {
      // Managed mode: upload to the server
      const formData = new FormData();
      formData.append('file', croppedBlob, 'image.jpg');

      const response = await fetch(this.options.uploadEndpoint, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(errorData.detail || 'Failed to upload image');
      }

      const data = await response.json() as { image_url?: string };
      this.options.onImageSelected?.(croppedBlob, { imageUrl: data.image_url ?? '' });
      return;
    }

    // Unmanaged mode: just pass the blob to the caller
    this.options.onImageSelected?.(croppedBlob);
  }

  /**
   * Switch between upload and URL tabs
   */
  private switchTab(tab: 'upload' | 'url'): void {
    const uploadPanel = document.getElementById(`${this.idPrefix}-upload-panel`);
    const urlPanel = document.getElementById(`${this.idPrefix}-url-panel`);
    const tabs = this.container.querySelectorAll<HTMLButtonElement>('[data-tab]');

    tabs.forEach(btn => {
      const isActive = btn.dataset.tab === tab;
      if (isActive) {
        btn.classList.add('border-blue-600', 'text-blue-600');
        btn.classList.remove('border-transparent', 'text-gray-500');
      } else {
        btn.classList.add('border-transparent', 'text-gray-500');
        btn.classList.remove('border-blue-600', 'text-blue-600');
      }
    });

    if (tab === 'upload') {
      uploadPanel?.classList.remove('hidden');
      urlPanel?.classList.add('hidden');
    } else {
      uploadPanel?.classList.add('hidden');
      urlPanel?.classList.remove('hidden');
    }

    this.clearError();
  }

  /**
   * Arm the remove button (first click) — highlights it and awaits a
   * confirming second click. A click anywhere else disarms it.
   */
  private armRemoveBtn(): void {
    if (!this.removeBtn) return;
    this.removeBtnArmed = true;
    this.removeBtn.classList.remove(...ImageUploader.REMOVE_BTN_NORMAL_CLASSES);
    this.removeBtn.classList.add(...ImageUploader.REMOVE_BTN_ARMED_CLASSES);
    this.removeBtn.title = 'Confirma l\'esborrat';

    this.outsideClickHandler = (e: MouseEvent) => {
      if (e.target !== this.removeBtn) {
        this.disarmRemoveBtn();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);
  }

  /**
   * Disarm the remove button, resetting its visual style.
   * Safe to call even when nothing is armed.
   */
  private disarmRemoveBtn(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (!this.removeBtn || !this.removeBtnArmed) return;
    this.removeBtnArmed = false;
    this.removeBtn.classList.remove(...ImageUploader.REMOVE_BTN_ARMED_CLASSES);
    this.removeBtn.classList.add(...ImageUploader.REMOVE_BTN_NORMAL_CLASSES);
    this.removeBtn.title = 'Esborra la imatge';
  }

  private async handleRemove(): Promise<void> {
    if (this.options.uploadEndpoint) {
      // Managed mode: DELETE on the server
      const response = await fetch(this.options.uploadEndpoint, {
        method: 'DELETE',
        headers: {
          ...authService.getAuthHeaders(),
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Delete failed' }));
        this.showError(errorData.detail || 'No s\'ha pogut esborrar la imatge');
        return;
      }
    }
    this.options.onImageRemoved?.();
  }

  // ─── Helpers ────────────────────────────────────────────────

  private showError(message: string): void {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }
  }

  private clearError(): void {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.classList.add('hidden');
    }
  }

  /**
   * Show error from outside (e.g. creation dialog form validation).
   * Switches to edit mode first if the error view isn't showing yet
   * (e.g. still in preview mode) so the message is never silently lost.
   */
  setError(message: string): void {
    if (!this.errorEl) {
      this.editingImage = true;
      this.render();
    }
    this.showError(message);
  }

  clearErrorExternal(): void {
    this.clearError();
  }

  /**
   * Clean up the outside-click listener, if the remove button is armed
   * when the host (dialog/popup) closes.
   */
  destroy(): void {
    this.disarmRemoveBtn();
  }
}
